import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  FileText, 
  Plus, 
  Trash2, 
  Eye, 
  Download, 
  Upload as UploadIcon,
  AlertTriangle, 
  Clock,
  X,
  FileIcon,
  Loader,
  CheckCircle,
  Search,
  Filter,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Calendar,
  Shield,
  Truck,
  FileCheck,
  FileX,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ClockIcon,
  RefreshCw,
  ArrowUpDown,
  DollarSign,
  Scale,
  Zap,
  AlertOctagon,
  Wrench,
  ScrollText,
  Award,
  MapPin,
  Leaf
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// Error message utility for user-friendly error handling
const getErrorMessage = (error, context = 'operation') => {
  // Handle different error types and provide user-friendly messages
  if (error?.response?.status) {
    switch (error.response.status) {
      case 400:
        return `Invalid request. Please check your input and try again.`;
      case 401:
        return `You're not authorized to perform this action. Please log in again.`;
      case 403:
        return `You don't have permission to perform this action.`;
      case 404:
        return `The requested document was not found. It may have been deleted.`;
      case 413:
        return `File too large. Please select a smaller file (max 10MB).`;
      case 415:
        return `File type not supported. Please upload a PDF, DOC, DOCX, JPG, or PNG file.`;
      case 422:
        return `Invalid file or missing required information. Please check your input.`;
      case 429:
        return `Too many requests. Please wait a moment and try again.`;
      case 500:
        return `Server error occurred. Please try again later.`;
      case 503:
        return `Service temporarily unavailable. Please try again later.`;
      default:
        return `An unexpected error occurred. Please try again.`;
    }
  }

  // Handle network errors
  if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
    return `Network connection failed. Please check your internet connection.`;
  }

  // Handle timeout errors
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return `Request timed out. Please try again.`;
  }

  // Context-specific default messages
  const contextMessages = {
    'load': 'Failed to load documents. Please refresh the page.',
    'upload': 'Failed to upload document. Please try again.',
    'delete': 'Failed to delete document. Please try again.',
    'download': 'Failed to download document. Please try again.',
    'operation': 'An error occurred. Please try again.'
  };

  return contextMessages[context] || contextMessages['operation'];
};

// Error display component
const ErrorAlert = ({ message, onClose }) => (
  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between">
    <div className="flex items-start">
      <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
      <div>
        <p className="text-sm text-red-800">{message}</p>
      </div>
    </div>
    {onClose && (
      <button
        onClick={onClose}
        className="text-red-400 hover:text-red-600 ml-4"
      >
        <X className="h-4 w-4" />
      </button>
    )}
  </div>
);

// Success display component
const SuccessAlert = ({ message, onClose }) => (
  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start justify-between">
    <div className="flex items-start">
      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
      <div>
        <p className="text-sm text-green-800">{message}</p>
      </div>
    </div>
    {onClose && (
      <button
        onClick={onClose}
        className="text-green-400 hover:text-green-600 ml-4"
      >
        <X className="h-4 w-4" />
      </button>
    )}
  </div>
);

const VEHICLE_DOCUMENT_TYPES = [
  { value: 'vehicle_registration', label: 'Vehicle Registration', required: true },
  { value: 'insurance_certificate', label: 'Insurance Certificate', required: true },
  { value: 'vehicle_title', label: 'Vehicle Title', required: true },
  { value: 'annual_inspection', label: 'DOT Inspection Report', required: false },
  { value: 'ifta_permit', label: 'IFTA Permit', required: false },
  { value: 'operating_authority', label: 'Operating Authority', required: false },
  { value: 'form_2290', label: 'Form 2290 (Heavy Vehicle Use Tax)', required: true },
  { value: 'ucr_registration', label: 'UCR Registration', required: true },
  { value: 'brake_inspection', label: 'Brake Inspection Certificate', required: false },
  { value: 'emission_test', label: 'Emission Test Certificate', required: false },
  { value: 'oversize_permit', label: 'Oversize/Overweight Permit', required: false },
  { value: 'hazmat_permit', label: 'Hazmat Permit', required: false },
  { value: 'safety_rating', label: 'Safety Rating Certificate', required: false },
  { value: 'roadside_inspection', label: 'Roadside Inspection Report', required: false },
  { value: 'maintenance_records', label: 'Maintenance Records', required: false },
  { value: 'lease_agreement', label: 'Vehicle Lease Agreement', required: false },
  { value: 'fuel_permit', label: 'Fuel Tax Permit', required: false },
  { value: 'carb_certificate', label: 'CARB Compliance Certificate', required: false },
  { value: 'weigh_station_cert', label: 'Weigh Station Certificate', required: false },
  { value: 'other_permit', label: 'Other Permit/License', required: false }
];

const US_STATES = [
  { value: '', label: 'Select State' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' }
];

const VehicleDocuments = ({ vehicleId, vehicleNumber, vehicle = {} }) => {
  const { company } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocumentType, setSelectedDocumentType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  
  // Document viewer state
  const [viewerDocument, setViewerDocument] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState(null);
  const [viewerUrl, setViewerUrl] = useState(null);
  
  // Expanded rows state
  const [expandedRows, setExpandedRows] = useState(new Set());

  const vehicleInfo = useMemo(() => vehicle || {}, [vehicle]);

  // Load documents for this vehicle
  const loadDocuments = useCallback(async () => {
    if (!vehicleId || !company?.id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/documents', {
        params: {
          category: 'vehicle',
          vehicleId: vehicleId,
          limit: 100
        }
      });

      setDocuments(response.documents || []);
    } catch (error) {
      console.error('Failed to load vehicle documents:', error);
      setError(getErrorMessage(error, 'load'));
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, company?.id]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Upload document
  const handleUploadDocument = async (formData) => {
    if (!vehicleId || !company?.id) return;

    try {
      setUploading(true);

      const uploadData = new FormData();
      uploadData.append('document', formData.file);
      uploadData.append('title', formData.title);
      uploadData.append('description', formData.description || '');
      uploadData.append('documentType', formData.documentType);
      uploadData.append('category', 'vehicle');
      uploadData.append('vehicleId', vehicleId);
      uploadData.append('companyId', company.id);
      uploadData.append('isRequired', formData.isRequired || false);
      uploadData.append('isCritical', formData.isCritical || false);
      // Send tags as array - only if there are tags (validator has default: [])
      const tags = formData.tags || [];
      if (tags.length > 0) {
        tags.forEach(tag => {
          uploadData.append('tags', tag);
        });
      }
      uploadData.append('notes', formData.notes || '');

      if (formData.expirationDate) {
        uploadData.append('expirationDate', new Date(formData.expirationDate).toISOString());
      }
      if (formData.issueDate) {
        uploadData.append('issueDate', new Date(formData.issueDate).toISOString());
      }
      if (formData.documentNumber) {
        uploadData.append('documentNumber', formData.documentNumber);
      }
      if (formData.issuingAuthority) {
        uploadData.append('issuingAuthority', formData.issuingAuthority);
      }
      if (formData.issuingState) {
        uploadData.append('issuingState', formData.issuingState);
      }

      const appendVehicleMetadataField = (field, value) => {
        if (value === undefined || value === null) {
          return;
        }

        const stringValue = typeof value === 'number' ? String(value) : `${value}`.trim();
        if (stringValue.length > 0) {
          uploadData.append(field, stringValue);
        }
      };

      const vehicleDisplayName = (() => {
        if (vehicleInfo.displayName) {
          return vehicleInfo.displayName;
        }

        const descriptor = [vehicleInfo.year, vehicleInfo.make, vehicleInfo.model]
          .filter(Boolean)
          .join(' ')
          .trim();

        if (vehicleInfo.vehicleNumber && descriptor) {
          return `${vehicleInfo.vehicleNumber} - ${descriptor}`;
        }

        if (vehicleInfo.vehicleNumber) {
          return vehicleInfo.vehicleNumber;
        }

        if (descriptor) {
          return descriptor;
        }

        return '';
      })();

      appendVehicleMetadataField('vehicleDisplayName', vehicleDisplayName);
      appendVehicleMetadataField('vehicleName', vehicleDisplayName);
      appendVehicleMetadataField('vehicleNumber', vehicleInfo.vehicleNumber || vehicleNumber);
      appendVehicleMetadataField('vehicleUnitNumber', vehicleInfo.unitNumber);
      appendVehicleMetadataField('vehiclePlateNumber', vehicleInfo.licensePlate || vehicleInfo.plateNumber);
      appendVehicleMetadataField('vehicleMake', vehicleInfo.make);
      appendVehicleMetadataField('vehicleModel', vehicleInfo.model);
      appendVehicleMetadataField('vehicleYear', vehicleInfo.year);
      appendVehicleMetadataField('vehicleVin', vehicleInfo.vin);

      const response = await api.post('/documents', uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      // Add new document to list
      setDocuments(prev => [response.document, ...prev]);
      setShowUploadModal(false);
      setSuccessMessage('Document uploaded successfully.');
      setError(null);
      
    } catch (error) {
      console.error('Failed to upload document:', error);
      setError(getErrorMessage(error, 'upload'));
    } finally {
      setUploading(false);
    }
  };

  // Delete document handlers
  const handleDeleteClick = (document) => {
    setDocumentToDelete(document);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    try {
      await api.delete(`/documents/${documentToDelete.id}`);
      setDocuments(prev => prev.filter(doc => doc.id !== documentToDelete.id));
      setShowDeleteModal(false);
      setDocumentToDelete(null);
      setSuccessMessage('Document deleted successfully.');
      setError(null);
    } catch (error) {
      console.error('Failed to delete document:', error);
      setError(getErrorMessage(error, 'delete'));
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDocumentToDelete(null);
  };

  // Document viewer functions
  const handleViewDocument = async (document) => {
    setError(null);
    setSuccessMessage(null);
    setViewerError(null);
    setViewerLoading(true);
    setViewerDocument(document);

    try {
      // First check if document already has a fileUrl
      if (document.fileUrl) {
        setViewerUrl(document.fileUrl);
        setViewerLoading(false);
        return;
      }

      // Otherwise, fetch the download URL
      const response = await api.get(`/documents/${document.id}/download`);
      const viewUrl = response?.download?.downloadUrl || response?.downloadUrl;
      
      if (!viewUrl) {
        throw new Error('Document URL not available.');
      }
      
      setViewerUrl(viewUrl);
    } catch (error) {
      console.error('Failed to get document URL:', error);
      setViewerError(getErrorMessage(error, 'load'));
    } finally {
      setViewerLoading(false);
    }
  };

  const closeViewer = () => {
    setViewerDocument(null);
    setViewerUrl('');
    setViewerError(null);
    setViewerLoading(false);
  };

  const openViewerInNewTab = () => {
    if (viewerUrl || viewerDocument?.fileUrl) {
      const url = viewerUrl || viewerDocument.fileUrl;
      window.open(url, '_blank');
    }
  };

  const resolveViewerContent = () => {
    const url = viewerUrl || viewerDocument?.fileUrl || '';
    if (!url) {
      return null;
    }

    const mimeType = viewerDocument?.mimeType || '';
    const fileName = viewerDocument?.originalFileName || viewerDocument?.fileName || '';

    const isImage = () => {
      if (mimeType.startsWith('image/')) {
        return true;
      }
      return /\.(png|jpg|jpeg|gif|webp|bmp)$/i.test(fileName);
    };

    const isPdf = () => {
      if (mimeType === 'application/pdf') {
        return true;
      }
      return /\.pdf$/i.test(fileName);
    };

    if (isImage()) {
      return (
        <div className="flex justify-center">
          <img
            src={url}
            alt={fileName || 'Document preview'}
            className="max-h-[70vh] object-contain"
            onError={() => setViewerError('Failed to load image')}
          />
        </div>
      );
    }

    // For PDFs, add parameters to disable toolbar and navigation that might trigger fullscreen
    const iframeUrl = isPdf() 
      ? `${url}#toolbar=0&navpanes=0&scrollbar=1&view=FitH&zoom=page-fit`
      : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

    return (
      <iframe
        title="Document viewer"
        src={iframeUrl}
        className="w-full h-[70vh] border"
        onError={() => setViewerError('Failed to load document')}
        allow="fullscreen"
      />
    );
  };

  // Download document
  const handleDownloadDocument = async (document) => {
    try {
      const response = await api.get(`/documents/${document.id}/download?binary=true`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: document.mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = document.originalFileName || document.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
      setError(getErrorMessage(error, 'download'));
    }
  };


  // Helper functions for status handling
  const getStatusDescriptor = (expirationDate) => {
    if (!expirationDate) {
      return {
        text: 'No Expiration',
        className: 'bg-gray-100 text-gray-700 border-gray-300'
      };
    }

    const today = new Date();
    const expDate = new Date(expirationDate);
    const diffDays = Math.ceil((expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { text: 'Expired', className: 'bg-red-100 text-red-800 border-red-300' };
    }
    if (diffDays <= 30) {
      return { text: 'Expiring Soon', className: 'bg-red-100 text-red-800 border-red-300' };
    }
    if (diffDays <= 60) {
      return { text: 'Expires Soon', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
    }

    return { text: 'Valid', className: 'bg-green-100 text-green-800 border-green-300' };
  };

  const getStatusBucket = (doc) => {
    const status = getStatusDescriptor(doc.expirationDate);
    
    if (status.text === 'Expired') {
      return 'expired';
    }
    if (status.text === 'Expiring Soon' || status.text === 'Expires Soon') {
      return 'expiring';
    }
    if (status.text === 'Valid') {
      return 'valid';
    }
    if (status.text === 'No Expiration') {
      return 'active';
    }
    return 'other';
  };

  const isDocumentExpiring = (expirationDate) => {
    if (!expirationDate) return false;
    const expiry = new Date(expirationDate);
    const now = new Date();
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const isDocumentExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Handle Firebase Timestamp format
    if (dateString && typeof dateString === 'object' && dateString._seconds) {
      const date = new Date(dateString._seconds * 1000 + (dateString._nanoseconds || 0) / 1000000);
      return date.toLocaleDateString();
    }
    
    // Handle regular date string/Date object
    return new Date(dateString).toLocaleDateString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Toggle row expansion
  const toggleRowExpansion = (documentId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(documentId)) {
        newSet.delete(documentId);
      } else {
        newSet.add(documentId);
      }
      return newSet;
    });
  };

  const getDocumentTypeColor = (type) => {
    const colors = {
      'vehicle_registration': 'bg-blue-100 text-blue-800',
      'insurance_certificate': 'bg-green-100 text-green-800',
      'vehicle_title': 'bg-purple-100 text-purple-800',
      'annual_inspection': 'bg-yellow-100 text-yellow-800',
      'ifta_permit': 'bg-gray-100 text-gray-800',
      'operating_authority': 'bg-indigo-100 text-indigo-800',
      'form_2290': 'bg-emerald-100 text-emerald-800',
      'ucr_registration': 'bg-cyan-100 text-cyan-800',
      'brake_inspection': 'bg-orange-100 text-orange-800',
      'emission_test': 'bg-green-100 text-green-800',
      'oversize_permit': 'bg-amber-100 text-amber-800',
      'hazmat_permit': 'bg-red-100 text-red-800',
      'safety_rating': 'bg-blue-100 text-blue-800',
      'roadside_inspection': 'bg-violet-100 text-violet-800',
      'maintenance_records': 'bg-orange-100 text-orange-800',
      'lease_agreement': 'bg-slate-100 text-slate-800',
      'fuel_permit': 'bg-yellow-100 text-yellow-800',
      'carb_certificate': 'bg-emerald-100 text-emerald-800',
      'weigh_station_cert': 'bg-stone-100 text-stone-800',
      'other_permit': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getDocumentTypeIcon = (type) => {
    const icons = {
      'vehicle_registration': <Truck className="h-4 w-4" />,
      'insurance_certificate': <Shield className="h-4 w-4" />,
      'vehicle_title': <FileText className="h-4 w-4" />,
      'annual_inspection': <CheckCircle className="h-4 w-4" />,
      'ifta_permit': <FileCheck className="h-4 w-4" />,
      'operating_authority': <FileText className="h-4 w-4" />,
      'form_2290': <DollarSign className="h-4 w-4" />,
      'ucr_registration': <ScrollText className="h-4 w-4" />,
      'brake_inspection': <Wrench className="h-4 w-4" />,
      'emission_test': <Leaf className="h-4 w-4" />,
      'oversize_permit': <Scale className="h-4 w-4" />,
      'hazmat_permit': <AlertOctagon className="h-4 w-4" />,
      'safety_rating': <Award className="h-4 w-4" />,
      'roadside_inspection': <MapPin className="h-4 w-4" />,
      'maintenance_records': <Wrench className="h-4 w-4" />,
      'lease_agreement': <ScrollText className="h-4 w-4" />,
      'fuel_permit': <Zap className="h-4 w-4" />,
      'carb_certificate': <Leaf className="h-4 w-4" />,
      'weigh_station_cert': <Scale className="h-4 w-4" />,
      'other_permit': <FileIcon className="h-4 w-4" />
    };
    return icons[type] || <FileText className="h-4 w-4" />;
  };

  // Filtering and sorting logic
  const documentTypes = useMemo(() => {
    const typeCounts = documents.reduce((acc, doc) => {
      const type = doc.documentType || 'other';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    const allTypes = [
      { id: 'all', name: 'All Documents', count: documents.length }
    ];

    VEHICLE_DOCUMENT_TYPES.forEach(type => {
      if (typeCounts[type.value] > 0) {
        allTypes.push({
          id: type.value,
          name: type.label,
          count: typeCounts[type.value]
        });
      }
    });

    return allTypes;
  }, [documents]);

  const statusSummary = useMemo(() => {
    const summary = {
      total: documents.length,
      active: 0,
      valid: 0,
      expiring: 0,
      expired: 0
    };

    documents.forEach((doc) => {
      const bucket = getStatusBucket(doc);
      if (bucket === 'active') {
        summary.active += 1;
      } else if (bucket === 'valid') {
        summary.valid += 1;
        summary.active += 1; // Valid documents are also counted as Active
      } else if (bucket === 'expiring') {
        summary.expiring += 1;
      } else if (bucket === 'expired') {
        summary.expired += 1;
      }
    });

    return summary;
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filtered = documents.filter((doc) => {
      // Filter by document type
      const matchesType = selectedDocumentType === 'all' || doc.documentType === selectedDocumentType;
      if (!matchesType) {
        return false;
      }

      // Filter by status
      const statusBucket = getStatusBucket(doc);
      if (selectedStatus !== 'all') {
        if (selectedStatus === 'active' && statusBucket !== 'active' && statusBucket !== 'valid') {
          return false;
        } else if (selectedStatus !== 'active' && statusBucket !== selectedStatus) {
          return false;
        }
      }

      // Filter by search term
      if (!normalizedSearch) {
        return true;
      }

      const searchableFields = [
        doc.title,
        doc.documentType,
        doc.documentNumber,
        doc.notes,
        doc.originalFileName,
        doc.fileName,
        doc.issuingAuthority,
        VEHICLE_DOCUMENT_TYPES.find(t => t.value === doc.documentType)?.label
      ];

      return searchableFields.some((field) =>
        typeof field === 'string' && field.toLowerCase().includes(normalizedSearch)
      );
    });

    // Sorting
    const getSortValue = (doc, key) => {
      switch (key) {
        case 'title':
          return (doc.title || '').toLowerCase();
        case 'documentType':
          return (VEHICLE_DOCUMENT_TYPES.find(t => t.value === doc.documentType)?.label || doc.documentType || '').toLowerCase();
        case 'status': {
          const statusWeight = {
            'Expired': 0,
            'Expiring Soon': 1,
            'Expires Soon': 2,
            'Valid': 3,
            'No Expiration': 4,
            'Unknown': 5
          };
          const status = getStatusDescriptor(doc.expirationDate);
          return statusWeight[status.text] ?? 6;
        }
        case 'expirationDate':
          return doc.expirationDate ? new Date(doc.expirationDate).getTime() : Number.POSITIVE_INFINITY;
        case 'createdAt':
        default:
          if (!doc.createdAt) return Number.NEGATIVE_INFINITY;
          
          // Handle Firebase Timestamp format
          if (doc.createdAt && typeof doc.createdAt === 'object' && doc.createdAt._seconds) {
            return doc.createdAt._seconds * 1000 + (doc.createdAt._nanoseconds || 0) / 1000000;
          }
          
          // Handle regular date string/Date object
          return new Date(doc.createdAt).getTime();
      }
    };

    const sorted = filtered.slice().sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);

      if (aValue === bValue) {
        return 0;
      }

      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [documents, searchTerm, selectedDocumentType, selectedStatus, sortConfig]);

  // Sorting handlers
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key, direction: 'asc' };
    });
  };

  // Get document stats
  const expiringDocs = filteredDocuments.filter(doc => isDocumentExpiring(doc.expirationDate));
  const expiredDocs = filteredDocuments.filter(doc => isDocumentExpired(doc.expirationDate));
  const requiredDocs = VEHICLE_DOCUMENT_TYPES.filter(type => type.required);
  const uploadedRequiredTypes = documents.map(doc => doc.documentType);
  const missingRequiredDocs = requiredDocs.filter(type => !uploadedRequiredTypes.includes(type.value));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading documents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Vehicle Documents</h3>
          <p className="text-sm text-gray-600">
            {vehicleNumber ? `Documents for ${vehicleNumber}` : 'Manage vehicle documentation'}
          </p>
        </div>
        <button 
          onClick={() => setShowUploadModal(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          disabled={uploading}
        >
          <Plus className="h-4 w-4 mr-2" />
          Upload Document
        </button>
      </div>

      {/* Search and Filter Controls */}
      {documents.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1">
              {/* Search Input */}
              <div className="relative w-full sm:max-w-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      onClick={() => setSearchTerm('')}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Document Type Filter */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Filter className="h-4 w-4 text-gray-500" />
                </div>
                <select
                  value={selectedDocumentType}
                  onChange={(e) => setSelectedDocumentType(e.target.value)}
                  className="block w-full sm:w-auto pl-10 pr-8 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium text-gray-700 appearance-none"
                >
                  {documentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name} ({type.count})
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Status Filter Ribbon */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs sm:text-sm text-blue-800">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedStatus('all')}
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border transition-all ${
                  selectedStatus === 'all'
                    ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                    : 'bg-white text-gray-700 border-blue-200 hover:bg-blue-100 hover:shadow-sm'
                }`}
              >
                <FileText className="h-3 w-3" />
                Total: {statusSummary.total}
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('active')}
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border transition-all ${
                  selectedStatus === 'active'
                    ? 'bg-cyan-600 text-white border-cyan-600 shadow-md'
                    : 'bg-white text-cyan-700 border-cyan-200 hover:bg-cyan-100 hover:shadow-sm'
                }`}
              >
                <CheckCircle2 className="h-3 w-3" />
                Active: {statusSummary.active}
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('valid')}
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border transition-all ${
                  selectedStatus === 'valid'
                    ? 'bg-green-600 text-white border-green-600 shadow-md'
                    : 'bg-white text-green-700 border-green-200 hover:bg-green-100 hover:shadow-sm'
                }`}
              >
                <FileCheck className="h-3 w-3" />
                Valid: {statusSummary.valid}
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('expiring')}
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border transition-all ${
                  selectedStatus === 'expiring'
                    ? 'bg-yellow-500 text-white border-yellow-500 shadow-md'
                    : 'bg-white text-yellow-600 border-yellow-200 hover:bg-yellow-100 hover:shadow-sm'
                }`}
              >
                <ClockIcon className="h-3 w-3" />
                Expiring Soon: {statusSummary.expiring}
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('expired')}
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border transition-all ${
                  selectedStatus === 'expired'
                    ? 'bg-red-600 text-white border-red-600 shadow-md'
                    : 'bg-white text-red-600 border-red-200 hover:bg-red-100 hover:shadow-sm'
                }`}
              >
                <XCircle className="h-3 w-3" />
                Expired: {statusSummary.expired}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Messages */}
      {error && (
        <ErrorAlert 
          message={error} 
          onClose={() => setError(null)} 
        />
      )}
      
      {successMessage && (
        <SuccessAlert 
          message={successMessage} 
          onClose={() => setSuccessMessage(null)} 
        />
      )}

      {missingRequiredDocs.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-3" />
            <div>
              <h4 className="text-sm font-medium text-red-800">Missing Required Documents</h4>
              <p className="text-sm text-red-700">
                The following required documents are missing: {missingRequiredDocs.map(doc => doc.label).join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}

      {expiredDocs.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-3" />
            <div>
              <h4 className="text-sm font-medium text-red-800">Expired Documents</h4>
              <p className="text-sm text-red-700">
                {expiredDocs.length} document(s) have expired and need to be renewed.
              </p>
            </div>
          </div>
        </div>
      )}

      {expiringDocs.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-yellow-600 mr-3" />
            <div>
              <h4 className="text-sm font-medium text-yellow-800">Documents Expiring Soon</h4>
              <p className="text-sm text-yellow-700">
                {expiringDocs.length} document(s) will expire within 30 days.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Documents List */}
      {documents.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents</h3>
          <p className="text-gray-600 mb-4">No documents have been uploaded for this vehicle yet.</p>
          <button 
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Upload First Document
          </button>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
          <p className="text-gray-600 mb-4">
            No documents match your current search and filter criteria.
          </p>
          <div className="flex justify-center space-x-3">
            <button 
              onClick={() => {
                setSearchTerm('');
                setSelectedDocumentType('all');
                setSelectedStatus('all');
              }}
              className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Clear Filters
            </button>
            <button 
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Upload Document
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Mobile Cards View */}
          <div className="block sm:hidden space-y-4">
            {filteredDocuments.map((document) => {
              const isExpiring = isDocumentExpiring(document.expirationDate);
              const isExpired = isDocumentExpired(document.expirationDate);
              
              return (
                <div key={document.id} className={`bg-white rounded-lg shadow-sm border p-4 ${
                  isExpired ? 'border-red-200' : isExpiring ? 'border-yellow-200' : ''
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 p-2 bg-gray-50 rounded-lg">
                        {getDocumentTypeIcon(document.documentType)}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">{document.title}</h4>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getDocumentTypeColor(document.documentType)}`}>
                          {getDocumentTypeIcon(document.documentType)}
                          {VEHICLE_DOCUMENT_TYPES.find(t => t.value === document.documentType)?.label || document.documentType}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteClick(document)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete Document"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Uploaded:</span>
                      <span className="text-gray-900">{formatDate(document.createdAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Size:</span>
                      <span className="text-gray-900">{formatFileSize(document.fileSize)}</span>
                    </div>
                    {document.expirationDate && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Expires:</span>
                        <span className={`${
                          isExpired ? 'text-red-600 font-medium' : 
                          isExpiring ? 'text-yellow-600 font-medium' : 'text-gray-900'
                        }`}>
                          {formatDate(document.expirationDate)}
                        </span>
                      </div>
                    )}
                    {document.documentNumber && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Document #:</span>
                        <span className="text-gray-900">{document.documentNumber}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-3 mt-4 pt-3 border-t border-gray-100">
                    <button 
                      onClick={() => handleViewDocument(document)}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      View
                    </button>
                    <button 
                      onClick={() => handleDownloadDocument(document)}
                      className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                    >
                      Download
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort('title')}
                        className="inline-flex items-center gap-2 px-2 py-1 rounded-md font-semibold uppercase tracking-wider text-xs transition-colors hover:text-blue-600 hover:bg-blue-50"
                      >
                        <FileText className="h-3 w-3" />
                        Document
                        {sortConfig.key === 'title' ? (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUp className="h-3 w-3 text-blue-600" /> : 
                            <ChevronDown className="h-3 w-3 text-blue-600" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-gray-400 opacity-50" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort('documentType')}
                        className="inline-flex items-center gap-2 px-2 py-1 rounded-md font-semibold uppercase tracking-wider text-xs transition-colors hover:text-blue-600 hover:bg-blue-50"
                      >
                        <Shield className="h-3 w-3" />
                        Type
                        {sortConfig.key === 'documentType' ? (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUp className="h-3 w-3 text-blue-600" /> : 
                            <ChevronDown className="h-3 w-3 text-blue-600" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-gray-400 opacity-50" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort('createdAt')}
                        className="inline-flex items-center gap-2 px-2 py-1 rounded-md font-semibold uppercase tracking-wider text-xs transition-colors hover:text-blue-600 hover:bg-blue-50"
                      >
                        <UploadIcon className="h-3 w-3" />
                        Upload Date
                        {sortConfig.key === 'createdAt' ? (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUp className="h-3 w-3 text-blue-600" /> : 
                            <ChevronDown className="h-3 w-3 text-blue-600" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-gray-400 opacity-50" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort('expirationDate')}
                        className="inline-flex items-center gap-2 px-2 py-1 rounded-md font-semibold uppercase tracking-wider text-xs transition-colors hover:text-blue-600 hover:bg-blue-50"
                      >
                        <Calendar className="h-3 w-3" />
                        Expiry Date
                        {sortConfig.key === 'expirationDate' ? (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUp className="h-3 w-3 text-blue-600" /> : 
                            <ChevronDown className="h-3 w-3 text-blue-600" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-gray-400 opacity-50" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDocuments.map((document) => {
                    const isExpiring = isDocumentExpiring(document.expirationDate);
                    const isExpired = isDocumentExpired(document.expirationDate);
                    
                    const isExpanded = expandedRows.has(document.id);
                    
                    return (
                      <React.Fragment key={document.id}>
                        <tr className={isExpired ? 'bg-red-50' : isExpiring ? 'bg-yellow-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <button
                                onClick={() => toggleRowExpansion(document.id)}
                                className="flex-shrink-0 p-1 mr-2 text-gray-400 hover:text-gray-600 transition-colors"
                                aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </button>
                              <div className="flex-shrink-0 p-2 bg-gray-50 rounded-lg mr-3">
                                {getDocumentTypeIcon(document.documentType)}
                              </div>
                              <div>
                                <div className="relative group" style={{ maxWidth: '250px' }}>
                                  <div className="text-sm font-medium text-gray-900 cursor-help">
                                    <span className="block truncate">
                                      {document.title && document.title.length > 30 
                                        ? `${document.title.substring(0, 30)}...` 
                                        : document.title}
                                    </span>
                                  </div>
                                  
                                  {/* Custom Tooltip Popup */}
                                  {document.title && document.title.length > 30 && (
                                    <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-in-out z-50 max-w-sm whitespace-normal break-words">
                                      <div className="relative">
                                        {document.title}
                                        {/* Tooltip Arrow */}
                                        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                {document.documentNumber && (
                                  <div className="text-sm text-gray-500">#{document.documentNumber}</div>
                                )}
                              </div>
                            </div>
                          </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getDocumentTypeColor(document.documentType)}`}>
                            {getDocumentTypeIcon(document.documentType)}
                            {VEHICLE_DOCUMENT_TYPES.find(t => t.value === document.documentType)?.label || document.documentType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(document.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center space-x-2">
                            {document.expirationDate ? (
                              <>
                                <span className={`${
                                  isExpired ? 'text-red-600 font-medium' : 
                                  isExpiring ? 'text-yellow-600 font-medium' : 'text-gray-900'
                                }`}>
                                  {formatDate(document.expirationDate)}
                                </span>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusDescriptor(document.expirationDate).className}`}>
                                  {getStatusDescriptor(document.expirationDate).text}
                                </span>
                              </>
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border bg-gray-100 text-gray-700 border-gray-300">
                                No Expiry
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-3">
                            <button 
                              onClick={() => handleViewDocument(document)}
                              className="text-blue-600 hover:text-blue-900" 
                              title="View Document"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleDownloadDocument(document)}
                              className="text-gray-600 hover:text-gray-900" 
                              title="Download Document"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(document)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Document"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded row with document details */}
                      {isExpanded && (
                        <tr className={`${isExpired ? 'bg-red-50' : isExpiring ? 'bg-yellow-50' : 'bg-gray-50'} border-t-0`}>
                          <td colSpan="4" className="px-6 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                              
                              {/* Basic Information */}
                              <div className="space-y-3">
                                <h4 className="font-semibold text-gray-900 border-b border-gray-200 pb-1">Document Information</h4>
                                
                                {document.description && (
                                  <div>
                                    <span className="font-medium text-gray-700">Description:</span>
                                    <p className="text-gray-600 mt-1">{document.description}</p>
                                  </div>
                                )}
                                
                                <div>
                                  <span className="font-medium text-gray-700">Original File:</span>
                                  <p className="text-gray-600">{document.originalFileName}</p>
                                </div>
                                
                                <div>
                                  <span className="font-medium text-gray-700">File Type:</span>
                                  <p className="text-gray-600">{document.mimeType}</p>
                                </div>
                                
                                <div>
                                  <span className="font-medium text-gray-700">File Size:</span>
                                  <p className="text-gray-600">{formatFileSize(document.fileSize)}</p>
                                </div>
                                
                                {document.version && (
                                  <div>
                                    <span className="font-medium text-gray-700">Version:</span>
                                    <p className="text-gray-600">{document.version}</p>
                                  </div>
                                )}
                              </div>

                              {/* Dates and Authority */}
                              <div className="space-y-3">
                                <h4 className="font-semibold text-gray-900 border-b border-gray-200 pb-1">Dates & Authority</h4>
                                
                                {document.issueDate && (
                                  <div>
                                    <span className="font-medium text-gray-700">Issue Date:</span>
                                    <p className="text-gray-600">{formatDate(document.issueDate)}</p>
                                  </div>
                                )}
                                
                                {document.effectiveDate && (
                                  <div>
                                    <span className="font-medium text-gray-700">Effective Date:</span>
                                    <p className="text-gray-600">{formatDate(document.effectiveDate)}</p>
                                  </div>
                                )}
                                
                                {document.issuingAuthority && (
                                  <div>
                                    <span className="font-medium text-gray-700">Issuing Authority:</span>
                                    <p className="text-gray-600">{document.issuingAuthority}</p>
                                  </div>
                                )}
                                
                                {document.issuingState && (
                                  <div>
                                    <span className="font-medium text-gray-700">Issuing State:</span>
                                    <p className="text-gray-600">{document.issuingState}</p>
                                  </div>
                                )}
                                
                                <div>
                                  <span className="font-medium text-gray-700">Updated:</span>
                                  <p className="text-gray-600">{formatDate(document.updatedAt)}</p>
                                </div>
                              </div>

                              {/* Requirements and Metadata */}
                              <div className="space-y-3">
                                <h4 className="font-semibold text-gray-900 border-b border-gray-200 pb-1">Requirements & Status</h4>
                                
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="flex items-center">
                                    <span className="font-medium text-gray-700">Required:</span>
                                    <span className={`ml-2 inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                      document.isRequired === 'true' || document.isRequired === true 
                                        ? 'bg-red-100 text-red-800' 
                                        : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {document.isRequired === 'true' || document.isRequired === true ? 'Yes' : 'No'}
                                    </span>
                                  </div>
                                  
                                  <div className="flex items-center">
                                    <span className="font-medium text-gray-700">Critical:</span>
                                    <span className={`ml-2 inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                      document.isCritical === 'true' || document.isCritical === true 
                                        ? 'bg-red-100 text-red-800' 
                                        : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {document.isCritical === 'true' || document.isCritical === true ? 'Yes' : 'No'}
                                    </span>
                                  </div>
                                </div>
                                
                                {document.renewalReminderDays && (
                                  <div>
                                    <span className="font-medium text-gray-700">Renewal Reminder:</span>
                                    <p className="text-gray-600">{document.renewalReminderDays} days before expiration</p>
                                  </div>
                                )}
                                
                                {document.renewalCost && (
                                  <div>
                                    <span className="font-medium text-gray-700">Renewal Cost:</span>
                                    <p className="text-gray-600">${document.renewalCost}</p>
                                  </div>
                                )}
                                
                                <div>
                                  <span className="font-medium text-gray-700">Uploaded By:</span>
                                  <p className="text-gray-600">{document.uploadedByName || document.uploadedByEmail}</p>
                                </div>
                                
                                {document.isVerified && (
                                  <div className="flex items-center">
                                    <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
                                    <span className="font-medium text-green-700">Verified</span>
                                    {document.verifiedBy && (
                                      <span className="text-gray-600 ml-1">by {document.verifiedBy}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Notes and Additional Info */}
                              {(document.notes || document.renewalNotes || document.tags?.length > 0) && (
                                <div className="md:col-span-2 lg:col-span-3 space-y-3 border-t border-gray-200 pt-3">
                                  <h4 className="font-semibold text-gray-900">Additional Information</h4>
                                  
                                  {document.notes && (
                                    <div>
                                      <span className="font-medium text-gray-700">Notes:</span>
                                      <p className="text-gray-600 whitespace-pre-wrap mt-1">{document.notes}</p>
                                    </div>
                                  )}
                                  
                                  {document.renewalNotes && (
                                    <div>
                                      <span className="font-medium text-gray-700">Renewal Notes:</span>
                                      <p className="text-gray-600 whitespace-pre-wrap mt-1">{document.renewalNotes}</p>
                                    </div>
                                  )}
                                  
                                  {document.tags && document.tags.length > 0 && (
                                    <div>
                                      <span className="font-medium text-gray-700">Tags:</span>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {document.tags.map((tag, index) => (
                                          <span key={index} className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                            {tag}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Document</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "{documentToDelete?.title}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewerDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4 py-6">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl p-4 sm:p-6">
            <button
              type="button"
              onClick={closeViewer}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              aria-label="Close viewer"
            >
              <X className="h-6 w-6" />
            </button>

            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {VEHICLE_DOCUMENT_TYPES.find(t => t.value === viewerDocument.documentType)?.label || viewerDocument.title}
              </h3>
              <p className="text-sm text-gray-500">
                {viewerDocument.originalFileName || viewerDocument.fileName || 'Document'}
              </p>
            </div>

            <div className="bg-gray-100 rounded-md p-4">
              {viewerLoading && (
                <div className="text-center text-gray-600 text-sm">Preparing viewer...</div>
              )}

              {!viewerLoading && viewerError && (
                <div className="text-center text-red-600 text-sm">{viewerError}</div>
              )}

              {!viewerLoading && !viewerError && resolveViewerContent()}
            </div>

            <div className="mt-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={openViewerInNewTab}
                className={`px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  viewerLoading || !(viewerUrl || viewerDocument?.fileUrl) ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                disabled={viewerLoading || !(viewerUrl || viewerDocument?.fileUrl)}
              >
                Open in New Tab
              </button>
              <button
                type="button"
                onClick={closeViewer}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <DocumentUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUploadDocument}
          uploading={uploading}
          vehicleNumber={vehicleNumber}
        />
      )}
    </div>
  );
};

// Document Upload Modal Component
const DocumentUploadModal = ({ isOpen, onClose, onUpload, uploading, vehicleNumber }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    documentType: 'vehicle_registration',
    file: null,
    expirationDate: '',
    issueDate: '',
    documentNumber: '',
    issuingAuthority: '',
    issuingState: '',
    isRequired: false,
    isCritical: false,
    notes: ''
  });

  const [dragActive, setDragActive] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateForm = () => {
    const errors = {};
    
    // Required field validations
    if (!formData.file) {
      errors.file = 'Document file is required';
    }
    
    if (!formData.title?.trim()) {
      errors.title = 'Document title is required';
    }
    
    if (!formData.documentType) {
      errors.documentType = 'Document type is required';
    }

    // File type validation
    if (formData.file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(formData.file.type)) {
        errors.file = 'Only PDF, JPG, and PNG files are allowed';
      }
      
      // File size validation (10MB)
      if (formData.file.size > 10 * 1024 * 1024) {
        errors.file = 'File size must be less than 10MB';
      }
    }

    // Date validations
    if (formData.expirationDate && formData.issueDate) {
      const issueDate = new Date(formData.issueDate);
      const expirationDate = new Date(formData.expirationDate);
      
      if (expirationDate <= issueDate) {
        errors.expirationDate = 'Expiration date must be after issue date';
      }
    }

    // Issue date cannot be in the future
    if (formData.issueDate) {
      const issueDate = new Date(formData.issueDate);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      
      if (issueDate > today) {
        errors.issueDate = 'Issue date cannot be in the future';
      }
    }

    // Document number format validation (basic)
    if (formData.documentNumber && formData.documentNumber.length > 50) {
      errors.documentNumber = 'Document number must be 50 characters or less';
    }

    // State validation
    if (formData.issuingState && formData.issuingState.length > 0) {
      const validState = US_STATES.find(state => state.value === formData.issuingState);
      if (!validState) {
        errors.issuingState = 'Please select a valid state';
      }
    }

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    setValidationErrors(errors);
    
    // Mark all fields as touched to show validation errors
    const allFields = Object.keys(formData);
    const touchedFields = {};
    allFields.forEach(field => touchedFields[field] = true);
    setTouched(touchedFields);

    if (Object.keys(errors).length === 0) {
      onUpload(formData);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ 
        ...prev, 
        file,
        title: prev.title || `${VEHICLE_DOCUMENT_TYPES.find(t => t.value === prev.documentType)?.label || prev.documentType} - ${vehicleNumber || 'Vehicle'}`
      }));
      
      // Clear file-related errors and mark as touched
      const newErrors = { ...validationErrors };
      delete newErrors.file;
      setValidationErrors(newErrors);
      setTouched(prev => ({ ...prev, file: true }));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setFormData(prev => ({ 
        ...prev, 
        file,
        title: prev.title || `${VEHICLE_DOCUMENT_TYPES.find(t => t.value === prev.documentType)?.label || prev.documentType} - ${vehicleNumber || 'Vehicle'}`
      }));
      
      // Clear file-related errors and mark as touched
      const newErrors = { ...validationErrors };
      delete newErrors.file;
      setValidationErrors(newErrors);
      setTouched(prev => ({ ...prev, file: true }));
    }
  };

  const handleDocumentTypeChange = (documentType) => {
    const docType = VEHICLE_DOCUMENT_TYPES.find(t => t.value === documentType);
    setFormData(prev => ({
      ...prev,
      documentType,
      isRequired: docType?.required || false,
      isCritical: docType?.required || false,
      title: `${docType?.label || documentType} - ${vehicleNumber || 'Vehicle'}`
    }));
    
    // Clear validation errors for affected fields
    const newErrors = { ...validationErrors };
    delete newErrors.documentType;
    delete newErrors.title;
    setValidationErrors(newErrors);
    setTouched(prev => ({ ...prev, documentType: true, title: true }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field when user types
    if (validationErrors[field]) {
      const newErrors = { ...validationErrors };
      delete newErrors[field];
      setValidationErrors(newErrors);
    }
    
    // Mark field as touched
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Run validation for this specific field
    const errors = validateForm();
    if (errors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: errors[field] }));
    }
  };

  // Helper function to determine if a field should show error styling
  const hasError = (field) => {
    return touched[field] && validationErrors[field];
  };

  // Helper function to get error message for a field
  const getErrorMessage = (field) => {
    return hasError(field) ? validationErrors[field] : '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Upload Vehicle Document</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={uploading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document File <span className="text-red-500">*</span>
            </label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center ${
                dragActive ? 'border-blue-400 bg-blue-50' : 
                hasError('file') ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {formData.file ? (
                <div className="flex items-center justify-center space-x-2">
                  <FileIcon className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{formData.file.name}</p>
                    <p className="text-sm text-gray-500">{(formData.file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
              ) : (
                <div>
                  <UploadIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag and drop your file here, or click to select
                  </p>
                  <p className="text-xs text-gray-500">
                    Supports: PDF, JPG, PNG (Max 10MB)
                  </p>
                </div>
              )}
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                id="document-upload"
              />
              <label
                htmlFor="document-upload"
                className="inline-block mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
              >
                Select File
              </label>
            </div>
            {getErrorMessage('file') && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1" />
                {getErrorMessage('file')}
              </p>
            )}
          </div>

          {/* Document Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.documentType}
              onChange={(e) => handleDocumentTypeChange(e.target.value)}
              onBlur={() => handleBlur('documentType')}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                hasError('documentType') 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              required
            >
              {VEHICLE_DOCUMENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label} {type.required ? '(Required)' : ''}
                </option>
              ))}
            </select>
            {getErrorMessage('documentType') && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1" />
                {getErrorMessage('documentType')}
              </p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              onBlur={() => handleBlur('title')}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                hasError('title') 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="Enter document title"
              required
            />
            {getErrorMessage('title') && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1" />
                {getErrorMessage('title')}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter document description"
            />
          </div>

          {/* Date Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Issue Date
              </label>
              <input
                type="date"
                value={formData.issueDate}
                onChange={(e) => handleInputChange('issueDate', e.target.value)}
                onBlur={() => handleBlur('issueDate')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                  hasError('issueDate') 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
              />
              {getErrorMessage('issueDate') && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  {getErrorMessage('issueDate')}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiration Date
              </label>
              <input
                type="date"
                value={formData.expirationDate}
                onChange={(e) => handleInputChange('expirationDate', e.target.value)}
                onBlur={() => handleBlur('expirationDate')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                  hasError('expirationDate') 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
              />
              {getErrorMessage('expirationDate') && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  {getErrorMessage('expirationDate')}
                </p>
              )}
            </div>
          </div>

          {/* Document Number and Issuing Authority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Number
              </label>
              <input
                type="text"
                value={formData.documentNumber}
                onChange={(e) => handleInputChange('documentNumber', e.target.value)}
                onBlur={() => handleBlur('documentNumber')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                  hasError('documentNumber') 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="Enter document number"
                maxLength={50}
              />
              {getErrorMessage('documentNumber') && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  {getErrorMessage('documentNumber')}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Issuing Authority
              </label>
              <input
                type="text"
                value={formData.issuingAuthority}
                onChange={(e) => handleInputChange('issuingAuthority', e.target.value)}
                onBlur={() => handleBlur('issuingAuthority')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                  hasError('issuingAuthority') 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="e.g., State DMV, DOT"
              />
              {getErrorMessage('issuingAuthority') && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  {getErrorMessage('issuingAuthority')}
                </p>
              )}
            </div>
          </div>

          {/* Issuing State */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Issuing State
            </label>
            <select
              value={formData.issuingState}
              onChange={(e) => handleInputChange('issuingState', e.target.value)}
              onBlur={() => handleBlur('issuingState')}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                hasError('issuingState') 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
            >
              {US_STATES.map(state => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
            {getErrorMessage('issuingState') && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1" />
                {getErrorMessage('issuingState')}
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes about this document"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <UploadIcon className="h-4 w-4 mr-2" />
                  Upload Document
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleDocuments;
